import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Users, Plus, Trash2, Pencil, Check, X, Loader2, Save, AlertCircle, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { initials } from "@/lib/utils-ros";

const ROLES = [
  { k: "economic_buyer", label: "Economic Buyer" },
  { k: "power_sponsor", label: "Power Sponsor" },
  { k: "champion", label: "Champion" },
  { k: "technical_evaluator", label: "Technical Evaluator" },
  { k: "operations", label: "Operations" },
  { k: "finance", label: "Finance" },
  { k: "procurement", label: "Procurement" },
  { k: "it", label: "IT" },
  { k: "legal", label: "Legal" },
  { k: "influencer", label: "Influencer" },
  { k: "user", label: "User" },
  { k: "detractor", label: "Detractor" },
  { k: "observer", label: "Observer" },
  { k: "unknown", label: "Unknown" },
];

const SUPPORT_LEVELS = [
  { k: "strong_advocate", label: "Strong advocate", color: "#15803D", w: 1.0 },
  { k: "supportive", label: "Supportive", color: "#84CC16", w: 0.8 },
  { k: "neutral", label: "Neutral", color: "#78909C", w: 0.0 },
  { k: "skeptical", label: "Skeptical", color: "#DC2626", w: -0.4 },
  { k: "detractor", label: "Detractor", color: "#991B1B", w: -1.0 },
];
const SUPPORT_MAP = Object.fromEntries(SUPPORT_LEVELS.map(s => [s.k, s]));

const EMPTY = () => ({
  name: "", title: "", email: "", phone: "", role: "unknown",
  influence: 50, support_level: "neutral", relationship_strength: 30,
  source_stage: "opportunity", inherited: false,
});

// Relationship softly modulates alignment — even an unmapped relationship keeps
// REL_FLOOR of a member's support weight (see backend/deal_control.py).
const REL_FLOOR = 0.4;

const CONTROL_KEY_ROLES = ["economic_buyer", "power_sponsor", "champion", "technical_evaluator", "procurement"];

/** Role coverage points — must match backend/deal_control.py (B-16). */
const ROLE_COVERAGE_PTS = {
  economic_buyer: 12,
  power_sponsor: 10,
  champion: 8,
  technical_evaluator: 7,
  procurement: 8,
};
const MAX_COVERAGE_PTS = 45;
const MAX_ALIGNMENT_PTS = 55;

/**
 * Deal Control Score (0–100) — B-16
 *
 * coverage_pts (0–45): points for each distinct key role mapped on the committee.
 * alignment_pts (0–55): influence-weighted support × relationship, scaled by breadth
 *   (a single contact cannot fully realise the alignment component).
 *
 * Caps: single member ≤45; no EB+PS ≤48; no EB ≤55; no PS ≤68.
 * See backend/deal_control.py for the canonical spec.
 */
export function computeControl(members = []) {
  const missingRoles = (ms) => {
    const roles = new Set(ms.map((m) => m.role));
    return CONTROL_KEY_ROLES.filter((r) => !roles.has(r));
  };
  if (!members.length) {
    return { score: 0, net: 0, positive: 0, negative: 0, missing: missingRoles([]), coveragePts: 0, alignmentPts: 0, capsApplied: [] };
  }

  const rolesPresent = new Set();
  let coveragePts = 0;
  members.forEach((m) => {
    if (ROLE_COVERAGE_PTS[m.role] != null && !rolesPresent.has(m.role)) {
      rolesPresent.add(m.role);
      coveragePts += ROLE_COVERAGE_PTS[m.role];
    }
  });
  coveragePts = Math.min(coveragePts, MAX_COVERAGE_PTS);

  let num = 0, den = 0, positive = 0, negative = 0;
  members.forEach((m) => {
    const infl = Math.max(0, Math.min(100, m.influence || 0)) / 100;
    const rel = Math.max(0, Math.min(100, m.relationship_strength || 0)) / 100;
    const w = SUPPORT_MAP[m.support_level]?.w ?? 0;
    const q = w * (REL_FLOOR + (1 - REL_FLOOR) * rel);
    num += infl * q;
    den += infl;
    const pts = Math.round(infl * q * 100);
    if (pts >= 0) positive += pts; else negative += pts;
  });

  const alignAvg = den > 0 ? num / den : 0;
  const alignNorm = Math.max(0, (alignAvg + 1) / 2);
  const breadth = Math.min(1, members.length / 2);
  const alignmentPts = Math.round(MAX_ALIGNMENT_PTS * alignNorm * (0.45 + 0.55 * breadth));

  let score = coveragePts + alignmentPts;
  const capsApplied = [];
  const hasEb = rolesPresent.has("economic_buyer");
  const hasPs = rolesPresent.has("power_sponsor");

  if (members.length === 1) { score = Math.min(score, 45); capsApplied.push("single_member"); }
  if (!hasEb && !hasPs) { score = Math.min(score, 48); capsApplied.push("no_economic_buyer_or_power_sponsor"); }
  else if (!hasEb) { score = Math.min(score, 55); capsApplied.push("no_economic_buyer"); }
  else if (!hasPs) { score = Math.min(score, 68); capsApplied.push("no_power_sponsor"); }

  score = Math.max(0, Math.min(100, score));
  return { score, net: positive + negative, positive, negative, missing: missingRoles(members), coveragePts, alignmentPts, capsApplied };
}

function saveErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join("; ");
  return "Failed to save buying committee";
}

export function BuyingCommitteeEditor({ opportunity, onSave, readOnly = false }) {
  const [members, setMembers] = useState(opportunity.buying_committee || []);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState(EMPTY());
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const saveSeq = useRef(0);

  const serverCommittee = useMemo(
    () => JSON.stringify(opportunity.buying_committee || []),
    [opportunity.buying_committee],
  );

  useEffect(() => {
    setMembers(opportunity.buying_committee || []);
    setDirty(false);
    setSaveError(null);
  }, [opportunity.id, serverCommittee]);

  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const persistMembers = useCallback(async (nextMembers, { manual = false } = {}) => {
    const seq = ++saveSeq.current;
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(nextMembers);
      if (seq !== saveSeq.current) return;
      setDirty(false);
      if (manual) toast.success("Buying committee saved");
    } catch (err) {
      if (seq !== saveSeq.current) return;
      const msg = saveErrorMessage(err);
      setSaveError(msg);
      setDirty(true);
      toast.error(msg);
      throw err;
    } finally {
      if (seq === saveSeq.current) setSaving(false);
    }
  }, [onSave]);

  const control = useMemo(() => computeControl(members), [members]);

  function startAdd() {
    setDraft(EMPTY());
    setEditingIdx(-1);
  }
  function startEdit(i) {
    setDraft({ ...members[i] });
    setEditingIdx(i);
  }
  function cancelEdit() {
    setEditingIdx(null);
    setDraft(EMPTY());
  }
  async function commitEdit() {
    if (!draft.name.trim()) return;
    const clean = {
      ...draft,
      name: draft.name.trim(),
      title: draft.title.trim(),
      email: draft.email?.trim() || null,
      phone: draft.phone?.trim() || null,
      influence: Number(draft.influence) || 0,
      relationship_strength: Number(draft.relationship_strength) || 0,
    };
    const next = editingIdx === -1
      ? [...members, clean]
      : members.map((x, i) => (i === editingIdx ? clean : x));
    setMembers(next);
    setEditingIdx(null);
    setDraft(EMPTY());
    setDirty(true);
    try {
      await persistMembers(next);
    } catch { /* toast + saveError set in persistMembers */ }
  }
  async function removeAt(i) {
    const next = members.filter((_, idx) => idx !== i);
    setMembers(next);
    setDirty(true);
    try {
      await persistMembers(next);
    } catch { /* toast + saveError set in persistMembers */ }
  }

  async function saveAll() {
    try {
      await persistMembers(members, { manual: true });
    } catch { /* surfaced in persistMembers */ }
  }

  return (
    <div className="border border-black/10 rounded-md bg-white" data-testid="buying-committee-editor">
      {/* Header + Control gauge */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-[#063F73] bg-[#07539A]">
        <Users size={13} className="!text-white" />
        <div className="text-[11px] uppercase tracking-widest !text-white font-semibold">Buying Committee</div>
        <span className="hidden xl:inline text-[9px] normal-case tracking-normal font-normal !text-[#B9DDF0]">Captured on this opportunity</span>
        <span className="ml-auto text-[10px] font-mono !text-[#B9DDF0]">{members.length} member{members.length === 1 ? "" : "s"}</span>
      </div>

      {/* Control gauge */}
      <div className="p-4 border-b border-[#BBD8E7] bg-[#EEF4F7]" data-testid="control-gauge">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-[10px] uppercase tracking-widest text-[#607D8B]">Control on this sale</div>
          <div className="font-mono text-[13px] tabular-nums text-[#07539A]" data-testid="control-score">{control.score}/100</div>
        </div>
        <div className="w-full h-1.5 bg-white rounded-sm overflow-hidden">
          <div
            className="h-full transition-all"
            style={{
              width: `${control.score}%`,
              background: control.score >= 60 ? "var(--success)" : control.score >= 30 ? "var(--copper)" : "var(--danger)",
            }}
          />
        </div>
        {control.capsApplied?.length > 0 && (
          <div className="mt-1.5 text-[10px] text-[#607D8B]" data-testid="control-caps">
            Score capped — {control.capsApplied.map((c) => c.replace(/_/g, " ")).join("; ")}
          </div>
        )}
        {control.missing?.length > 0 && (
          <div className="mt-2 text-[10px] text-[var(--warning)]">
            Missing key roles: <span className="font-medium">{control.missing.map(r => ROLES.find(x => x.k === r)?.label).filter(Boolean).join(" · ")}</span>
          </div>
        )}
      </div>

      {/* Member list */}
      <div className="p-3 space-y-3 bg-white">
        {members.map((m, i) => (
          <MemberRow
            key={`${m.name}-${m.role}-${i}`} m={m} idx={i}
            editing={editingIdx === i}
            draft={draft} setDraft={setDraft}
            onStartEdit={() => startEdit(i)}
            onCancel={cancelEdit}
            onCommit={commitEdit}
            onRemove={() => removeAt(i)}
            readOnly={readOnly}
          />
        ))}
        {!readOnly && editingIdx === -1 && (
          <MemberRow
            m={draft} idx={-1} editing
            draft={draft} setDraft={setDraft}
            onCancel={cancelEdit} onCommit={commitEdit}
          />
        )}
        {members.length === 0 && editingIdx !== -1 && (
          <div className="p-6 text-center text-[12px] text-[var(--text-muted)]">
            No committee members yet. Add one to unlock deal-control insights.
          </div>
        )}
      </div>

      {/* Actions */}
      {!readOnly && (
      <div className="flex items-center justify-between gap-2 p-3 border-t border-black/10">
        <button
          onClick={startAdd}
          disabled={editingIdx !== null}
          data-testid="committee-add"
          className="h-9 px-3 rounded-md border border-black/15 hover:border-[var(--saarv-blue)] hover:text-[var(--saarv-blue-strong)] text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-50 text-[var(--text)]"
        >
          <Plus size={12} /> Add member
        </button>
        <div className="flex items-center gap-2">
          {saveError && (
            <span className="text-[11px] text-[var(--danger)] flex items-center gap-1 max-w-[12rem]" data-testid="committee-save-error">
              <AlertCircle size={11} className="shrink-0" /> {saveError}
            </span>
          )}
          <button
            onClick={saveAll}
            disabled={!dirty || saving}
            data-testid="committee-save"
            className="h-9 px-4 rounded-md bg-[var(--saarv-blue)] hover:bg-[var(--saarv-blue-strong)] text-white text-[12px] font-medium flex items-center gap-2 disabled:opacity-40"
          >
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            {saving ? "Saving…" : dirty ? "Retry save" : "Saved"}
          </button>
        </div>
      </div>
      )}
    </div>
  );
}

function MemberRow({ m, idx, editing, draft, setDraft, onStartEdit, onCancel, onCommit, onRemove, readOnly = false }) {
  if (editing) {
    return (
      <div className="p-3 bg-[var(--saarv-blue-soft)]/40" data-testid={idx === -1 ? "committee-new-row" : `committee-edit-${idx}`}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input
            value={draft.name}
            onChange={e => setDraft({ ...draft, name: e.target.value })}
            placeholder="Full name"
            data-testid="cm-name"
            className="h-9 bg-white border border-black/15 rounded-md px-3 text-[13px] text-[var(--text)]"
          />
          <input
            value={draft.title}
            onChange={e => setDraft({ ...draft, title: e.target.value })}
            placeholder="Designation (e.g. VP Operations)"
            data-testid="cm-title"
            className="h-9 bg-white border border-black/15 rounded-md px-3 text-[13px] text-[var(--text)]"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <input
            type="email"
            value={draft.email || ""}
            onChange={e => setDraft({ ...draft, email: e.target.value })}
            placeholder="Email"
            data-testid="cm-email"
            className="h-9 bg-white border border-black/15 rounded-md px-3 text-[13px] text-[var(--text)]"
          />
          <input
            type="tel"
            value={draft.phone || ""}
            onChange={e => setDraft({ ...draft, phone: e.target.value })}
            placeholder="Phone"
            data-testid="cm-phone"
            className="h-9 bg-white border border-black/15 rounded-md px-3 text-[13px] text-[var(--text)]"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[var(--text-dim)] mb-1">Role for us</div>
            <select
              value={draft.role}
              onChange={e => setDraft({ ...draft, role: e.target.value })}
              data-testid="cm-role"
              className="w-full h-9 bg-white border border-black/15 rounded-md px-2 text-[13px] text-[var(--text)]"
            >
              {ROLES.map(r => <option key={r.k} value={r.k}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-widest text-[var(--text-dim)] mb-1">Support level</div>
            <select
              value={draft.support_level}
              onChange={e => setDraft({ ...draft, support_level: e.target.value })}
              data-testid="cm-support"
              className="w-full h-9 bg-white border border-black/15 rounded-md px-2 text-[13px] text-[var(--text)]"
            >
              {SUPPORT_LEVELS.map(s => <option key={s.k} value={s.k}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <SliderField label="Influence in org" value={draft.influence} onChange={v => setDraft({ ...draft, influence: v })} testid="cm-influence" />
          <SliderField label="Our relationship strength" value={draft.relationship_strength} onChange={v => setDraft({ ...draft, relationship_strength: v })} testid="cm-relationship" />
        </div>
        <div className="mt-3 flex items-center gap-2 justify-end">
          <button onClick={onCancel} data-testid="cm-cancel" className="h-8 px-3 text-[12px] text-[var(--text-muted)] hover:text-[var(--saarv-blue)] flex items-center gap-1">
            <X size={11} /> Cancel
          </button>
          <button onClick={onCommit} disabled={!draft.name.trim()} data-testid="cm-commit" className="h-8 px-3 rounded-md bg-[var(--saarv-blue)] hover:bg-[var(--saarv-blue-strong)] text-white text-[12px] font-medium flex items-center gap-1 disabled:opacity-40">
            <Check size={11} /> {idx === -1 ? "Add" : "Update"}
          </button>
        </div>
      </div>
    );
  }
  const sup = SUPPORT_MAP[m.support_level] || SUPPORT_MAP.neutral;
  return (
    <div className="p-3 rounded-[16px] border border-[#BBD8E7] bg-[#E7F5FB] shadow-[0_5px_14px_rgba(7,83,154,0.06)] hover:border-[#75B8D7] transition-colors flex items-start gap-3" data-testid={`committee-${idx}`}>
      <div className="w-9 h-9 rounded-full bg-[#E7F5FB] border border-[#75B8D7] flex items-center justify-center text-[11px] font-mono shrink-0 text-[#07539A]">
        {initials(m.name)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-medium">{m.name}</span>
          <span className="text-[11px] text-[var(--text-muted)]">·</span>
          <span className="text-[11px] text-[var(--text-muted)]">{m.title || "—"}</span>
        </div>
        {(m.email || m.phone) && (
          <div className="mt-1 flex items-center gap-3 flex-wrap text-[11px] text-[var(--text-muted)]">
            {m.email && (
              <a href={`mailto:${m.email}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 hover:text-[var(--saarv-blue)]" data-testid={`cm-email-${idx}`}>
                <Mail size={10} /> {m.email}
              </a>
            )}
            {m.phone && (
              <a href={`tel:${m.phone}`} onClick={e => e.stopPropagation()} className="flex items-center gap-1 hover:text-[var(--saarv-blue)]" data-testid={`cm-phone-${idx}`}>
                <Phone size={10} /> {m.phone}
              </a>
            )}
          </div>
        )}
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-full border border-[#8EC9E3] bg-[#DDF2FB] text-[#07539A] font-mono">
            {ROLES.find(r => r.k === m.role)?.label || m.role}
          </span>
          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm font-mono" style={{ background: `${sup.color}15`, color: sup.color, border: `1px solid ${sup.color}30` }}>
            {sup.label}
          </span>
        </div>
        <div className="mt-2 grid grid-cols-2 gap-3 max-w-md">
          <TinyBar label="Influence" value={m.influence} />
          <TinyBar label="Relationship" value={m.relationship_strength} />
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {!readOnly && (
          <>
            <button onClick={onStartEdit} data-testid={`cm-edit-${idx}`} className="h-7 w-7 rounded flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--saarv-blue)] hover:bg-black/[0.03]" title="Edit">
              <Pencil size={12} />
            </button>
            <button onClick={onRemove} data-testid={`cm-remove-${idx}`} className="h-7 w-7 rounded flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--danger)] hover:bg-black/[0.03]" title="Remove">
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function SliderField({ label, value, onChange, testid }) {
  const color = scoreColor(value);
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] uppercase tracking-widest text-[var(--text-dim)]">{label}</span>
        <span className="font-mono text-[11px] tabular-nums font-semibold" style={{ color }}>{value}%</span>
      </div>
      <input
        type="range" min={0} max={100} step={5}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-[var(--saarv-blue)]"
      />
    </div>
  );
}

function scoreColor(value) {
  if (value >= 70) return "#15803D";
  if (value >= 40) return "#E28B21";
  return "#B42318";
}

function TinyBar({ label, value }) {
  const color = scoreColor(value);
  return (
    <div className="min-w-0">
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="text-[9px] uppercase tracking-wider text-[var(--text-dim)] whitespace-nowrap">{label}</span>
        <span className="font-mono text-[10px] shrink-0 text-right font-semibold" style={{ color }}>{value}%</span>
      </div>
    </div>
  );
}
